/**
 * SOVRANOVA — CouplesFloor
 *
 * Two people, one device, one microphone. This is the control that decides who
 * is speaking.
 *
 * WHY A FLOOR CONTROL RATHER THAN AN OPEN MIC:
 * an open mic needs speaker diarisation to know who said what, and diarisation
 * degrades exactly where it matters most — on overlapping speech, which is what
 * a couple in conflict produces constantly. Guessing gets it wrong and then
 * writes the wrong name into a permanent transcript; misattributing "you never
 * listen to me" to the wrong partner is a real harm and they will never trust
 * the record again.
 *
 * Taking turns removes the guess entirely. It also happens to be how couples
 * therapy actually works — the talking stick, Imago dialogue, "reflect back
 * what you heard before you answer". The constraint the technology imposes is
 * the same one a good facilitator imposes on purpose, so it is presented as the
 * method, not as an apology.
 *
 * NOTHING HERE RECORDS ANYONE. Capture runs through services/voiceTurn, which
 * transcribes once and destroys the audio. See that file for the full promise.
 */
import { useState, useRef, useEffect } from 'react'
import { beginCapture, micSupported } from '../../services/voiceTurn'
import { api } from '../../services/api'
import { SignaturePad } from '../common/SignaturePad'

// Shown if the notice fetch fails — same terms, so the card still informs.
// The server copy (ConsentService COUPLES_NOTICE) is the source of truth.
const FALLBACK_AGREEMENT = {
  version: null,
  title: 'Couples session agreement',
  intro: 'Before a couples session begins, both partners agree to how it works and what is kept.',
  // Mirrors ConsentService COUPLES_NOTICE v2 (couples-session-v2, Sep 2026).
  points: [
    'Your voices are never recorded. What each of you says is turned into text on the spot and the audio is discarded immediately — it is never saved anywhere.',
    'What is kept: the written transcript of this session and a short summary of it, stored in the account holder’s account. The account holder can look back on it. Your companion uses the summary only in future couples sessions — never in either partner’s private, individual sessions.',
    'Reflections: saving a couples session into Reflections is switched off during the beta while a proper record of both partners’ consent is built. Nothing from this session is filed into Reflections.',
    'Your rights: either of you can stop at any time by ending the session. There is not yet a way to delete a single couples session on its own; the account holder can delete their whole account from Settings.',
  ],
  affirmation: 'I understand what is kept from this session and how it will be used, and I agree to take part.',
}

export function CouplesFloor({ names, onSetup, floorHolder, onFloor, onTurn, busy }) {
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [recording,    setRecording]    = useState(null)   // name currently holding the mic
  const [transcribing, setTranscribing] = useState(false)
  const [err,          setErr]          = useState(null)
  const captureRef = useRef(null)

  // ── Signed agreement (19 Aug) ─────────────────────────────────────────────
  // The transcript of a couples session is SHARED material that can end up in
  // Reflections — that deserves a signed record from both partners, not just a
  // paragraph of small print (David's request). Both sign before the session
  // can begin; the record is stored via the existing consent register with its
  // own notice version, so it can never satisfy the audio-capture gate.
  const [agreement,  setAgreement]  = useState(null)
  const [sigA,       setSigA]       = useState(null)
  const [sigB,       setSigB]       = useState(null)
  const [saving,     setSaving]     = useState(false)
  const [consentErr, setConsentErr] = useState(null)
  useEffect(() => {
    if (names) return
    let alive = true
    api.get('/ambient/consent/notice?kind=couples')
      .then(n => { if (alive && n?.points) setAgreement(n) })
      .catch(() => { /* fallback below */ })
    return () => { alive = false }
  }, [names])

  const recordAgreement = async () => {
    const participants = [
      { name: a.trim(), role: 'partner', method: 'in_app_signature', consented: true, signature: sigA },
      { name: b.trim(), role: 'partner', method: 'in_app_signature', consented: true, signature: sigB },
    ]
    const body = { participants, method: 'in_app_signature', kind: 'couples' }
    try { await api.post('/ambient/consent', body); return true }
    catch (_) {
      // One retry (commonest failure: token mid-refresh), then proceed with an
      // honest note — the agreement happened in the room; only the durable
      // record failed, and blocking the session on infrastructure would be
      // worse than noting it.
      try { await new Promise(r => setTimeout(r, 1200)); await api.post('/ambient/consent', body); return true }
      catch (_) { return false }
    }
  }

  // Never leave the microphone open if this unmounts mid-turn.
  useEffect(() => () => { try { captureRef.current?.cancel() } catch {} }, [])

  // ── Setup: names + the signed session agreement ───────────────────────────
  if (!names) {
    const notice = agreement || FALLBACK_AGREEMENT
    const ready  = a.trim() && b.trim() && sigA && sigB && !saving
    const begin  = async () => {
      if (!ready) return
      setSaving(true); setConsentErr(null)
      const ok = await recordAgreement()
      if (!ok) setConsentErr("The signed record couldn't be saved just now — your session still works; the agreement stands between you.")
      setSaving(false)
      onSetup({ a: a.trim(), b: b.trim() })
    }
    return (
      <div style={{ ...s.setup, maxHeight:'72vh', overflowY:'auto' }}>
        <p style={s.setupTitle}>Before you begin</p>
        <p style={s.setupBody}>
          Your companion will speak to you both by name, so it can reflect each of you back properly
          rather than talking to "the couple".
        </p>
        <div style={s.nameRow}>
          <input style={s.nameInput} value={a} maxLength={24} placeholder="First name"
            onChange={e => setA(e.target.value)} aria-label="First partner's name" />
          <span style={s.amp}>and</span>
          <input style={s.nameInput} value={b} maxLength={24} placeholder="First name"
            onChange={e => setB(e.target.value)} aria-label="Second partner's name" />
        </div>

        {/* The agreement, said plainly where both people can read it — then
            SIGNED by each. This transcript is shared material that can be
            filed to Reflections; a signature each is the honest bar for that. */}
        <p style={s.agreeTitle}>{notice.title}</p>
        {notice.points.map((p, i) => <p key={i} style={s.agreePoint}>• {p}</p>)}
        <p style={s.affirm}>{notice.affirmation}</p>

        <p style={s.sigLabel}>{a.trim() || 'First partner'} — sign here</p>
        <SignaturePad value={sigA} onChange={setSigA} tone="light" />
        <p style={s.sigLabel}>{b.trim() || 'Second partner'} — sign here</p>
        <SignaturePad value={sigB} onChange={setSigB} tone="light" />

        {consentErr && <p style={s.consentErr}>{consentErr}</p>}
        <button style={{ ...s.ready, ...(ready ? {} : s.readyOff) }} disabled={!ready}
          onClick={begin}>
          {saving ? 'Saving your agreement…' : "We've both signed — begin"}
        </button>
      </div>
    )
  }

  if (!micSupported()) {
    return (
      <p style={s.note}>
        This browser won't give SOVRANOVA a microphone — you can still type. Tap a name first so
        it knows who's speaking.
      </p>
    )
  }

  const people = [names.a, names.b]

  // Stop whoever is speaking, transcribe their turn, and optionally hand the
  // floor straight to the other person in the same tap.
  const finish = async (next = null) => {
    const speaker = recording
    const cap = captureRef.current
    captureRef.current = null
    setRecording(null)
    if (!cap) return
    setTranscribing(true)
    try {
      const text = await cap.stop()
      if (text) onTurn(speaker, text)
    } catch (e) {
      setErr(e?.name === 'NotAllowedError'
        ? 'Microphone permission was denied.'
        : "Couldn't catch that one — try again, or type it instead.")
    } finally {
      setTranscribing(false)
    }
    if (next) start(next)
  }

  const start = async (name) => {
    setErr(null)
    onFloor?.(name)
    try {
      captureRef.current = await beginCapture()
      setRecording(name)
    } catch (e) {
      captureRef.current = null
      setErr(e?.name === 'NotAllowedError'
        ? 'Microphone permission was denied — you can still type.'
        : 'Could not open the microphone.')
    }
  }

  const tap = (name) => {
    if (transcribing || busy) return
    if (recording === name) return finish()          // same name = "I'm done"
    if (recording)          return finish(name)      // other name = pass the floor
    start(name)
  }

  return (
    <div style={s.wrap}>
      <div style={s.row}>
        {people.map(name => {
          const live = recording === name
          const held = !recording && floorHolder === name
          return (
            <button key={name} onClick={() => tap(name)} disabled={transcribing || busy}
              style={{ ...s.seat, ...(live ? s.seatLive : held ? s.seatHeld : {}) }}
              title={live ? `${name} is speaking — tap when done` : `Tap when ${name} speaks`}>
              <span style={s.seatDot}>{live ? '●' : '🎙'}</span>
              <span style={s.seatName}>{name}</span>
              <span style={s.seatHint}>{live ? 'speaking — tap when done' : 'tap to speak'}</span>
            </button>
          )
        })}
      </div>
      <p style={s.status}>
        {transcribing ? 'Writing that down…'
          : recording ? `Listening to ${recording}. Tap ${recording} when done, or tap the other name to pass over.`
          : 'Whoever is speaking, tap their name first — that keeps the transcript straight.'}
      </p>
      {err && <p style={s.err}>{err}</p>}
    </div>
  )
}

const s = {
  setup:      { display:'flex', flexDirection:'column', gap:'0.6rem', background:'var(--cream,#f6f2e9)', border:'1px solid rgba(201,168,76,.35)', borderRadius:'0.9rem', padding:'0.95rem 1rem', margin:'0.4rem 0' },
  setupTitle: { fontSize:'0.7rem', letterSpacing:'0.11em', textTransform:'uppercase', fontWeight:700, color:'#b08d3a', margin:0 },
  setupBody:  { fontSize:'0.86rem', lineHeight:1.6, color:'var(--ink,#3a352c)', margin:0 },
  nameRow:    { display:'flex', alignItems:'center', gap:'0.5rem' },
  nameInput:  { flex:1, minWidth:0, border:'1px solid rgba(0,0,0,.16)', borderRadius:'0.6rem', padding:'0.6rem 0.7rem', fontSize:'0.95rem', fontFamily:'inherit', background:'#fff', color:'var(--ink,#2b2b2b)' },
  amp:        { fontSize:'0.8rem', color:'var(--mid-grey,#9a948a)', flexShrink:0 },
  consent:    { fontSize:'0.8rem', lineHeight:1.6, color:'var(--mid-grey,#6f6a61)', margin:0, borderTop:'1px solid rgba(0,0,0,.08)', paddingTop:'0.6rem' },
  agreeTitle: { fontSize:'0.72rem', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:700, color:'#b08d3a', margin:'0.35rem 0 0', borderTop:'1px solid rgba(0,0,0,.08)', paddingTop:'0.7rem' },
  agreePoint: { fontSize:'0.78rem', lineHeight:1.55, color:'var(--mid-grey,#6f6a61)', margin:0 },
  affirm:     { fontSize:'0.8rem', lineHeight:1.55, color:'var(--ink,#3a352c)', fontWeight:600, margin:'0.25rem 0 0' },
  sigLabel:   { fontSize:'0.78rem', fontWeight:700, color:'var(--ink,#3a352c)', margin:'0.5rem 0 0' },
  consentErr: { fontSize:'0.76rem', lineHeight:1.5, color:'#a05c20', margin:0 },
  ready:      { marginTop:'0.1rem', padding:'0.8rem 1rem', border:'none', borderRadius:'0.75rem', background:'linear-gradient(135deg,#5a3a9e,#3a2070)', color:'#fff', fontSize:'0.95rem', fontWeight:700, fontFamily:'inherit', cursor:'pointer' },
  readyOff:   { opacity:0.45, cursor:'not-allowed' },

  wrap:   { display:'flex', flexDirection:'column', gap:'0.35rem', padding:'0.3rem 0 0.1rem', flexShrink:0 },
  row:    { display:'flex', gap:'0.5rem' },
  seat:   { flex:1, minWidth:0, display:'flex', flexDirection:'column', alignItems:'center', gap:'0.1rem', padding:'0.6rem 0.5rem', borderRadius:'0.8rem', border:'1px solid rgba(0,0,0,.14)', background:'transparent', cursor:'pointer', fontFamily:'inherit', color:'var(--ink,#2b2b2b)' },
  seatHeld:{ borderColor:'var(--gold,#c9a84c)', background:'rgba(201,168,76,.10)' },
  seatLive:{ borderColor:'#c0392b', background:'rgba(192,57,43,.10)' },
  seatDot: { fontSize:'1rem', lineHeight:1 },
  seatName:{ fontSize:'0.92rem', fontWeight:700, maxWidth:'100%', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' },
  seatHint:{ fontSize:'0.66rem', color:'var(--mid-grey,#9a948a)' },
  status: { textAlign:'center', fontSize:'0.72rem', color:'var(--mid-grey,#9a948a)', margin:0, lineHeight:1.5 },
  err:    { textAlign:'center', fontSize:'0.78rem', color:'#c0392b', margin:0 },
  note:   { textAlign:'center', fontSize:'0.78rem', color:'var(--mid-grey,#9a948a)', margin:'0.3rem 0' },
}
